// ============================================================================
// LES CONDITIONS & COORDONNÉES D'UN FOURNISSEUR — le bandeau de pastilles et le panneau de saisie de sa carte.
//
// UTILITÉ : remise, port et escompte pré-remplissent chaque nouvelle commande du fournisseur (modifiables
// commande par commande) ; l'e-mail recevra le bon de commande ; le compte attend son lecteur.
//
// ⚠️ ON ÉCRIT À LA SORTIE DU CHAMP, PAS À LA FRAPPE (la maquette écrit à chaque touche) : « 2, » est un
//    état transitoire de « 2,5 », et l'écrire enverrait au journal une remise que personne n'a voulue.
//    Même règle que la case de coefficient voisine.
// ⚠️ ON RELIT APRÈS CHAQUE ÉCRITURE : c'est le producteur qui décide (refus, normalisation). Afficher la
//    saisie comme si elle était enregistrée ferait croire à une remise que le journal n'a pas acceptée.
// ⚠️ LA RÈGLE EST CELLE DU CONTRAT (`supplierTermsFieldRefusal`) : l'écran prévient avec elle, le serveur la
//    rejoue. Une valeur refusée ici n'est pas envoyée, et le champ dit pourquoi — dans la langue du compte :
//    la règle rend un CODE, l'écran en fait sa phrase traduite.
// ============================================================================
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { formatRateBp, parseRateBp } from '@orgazone/core-vat';
import { currentCurrency, parseMinor, toInputValue } from '@orgazone/money-format';
import {
  SUPPLIER_TERMS_LIMITS, supplierAccountHint, supplierTermsFieldRefusal,
  type SupplierTermsField, type SupplierTermsFields, type SupplierTermsRefusal,
} from '@orgazone/sync-contract';
import { Icon, showToast, Trans } from '@orgazone/ui-web';
import type { ReferentialSupplier } from '../api';
import { ChampCoef, coefAffiche, coeffInput } from './champ-coefficient';
import { euros } from './titres';
import { t } from '../i18n';

/** Une pastille du bandeau : ce qui est saisi, en une ligne. */
export interface TermsChip {
  key: string;
  label: string;
  tone: 'violet' | 'teal' | 'amber' | 'blue' | 'rose' | 'ink';
  icon: string;
}

const TONES: Record<TermsChip['tone'], { bg: string; fg: string }> = {
  violet: { bg: 'var(--violet-bg)', fg: 'var(--violet-fg)' },
  teal: { bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  amber: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
  blue: { bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  rose: { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)' },
  ink: { bg: 'var(--ink-100)', fg: 'var(--ink-700)' },
};

/** Les deux initiales de la tuile — les premières lettres des deux premiers mots. */
export function supplierInitials(label: string): string {
  return label.trim().split(/\s+/).filter(Boolean).map((w) => w[0]!).join('').slice(0, 2).toUpperCase();
}

/**
 * La teinte de la tuile, DÉRIVÉE DU CODE.
 * ⚠️ La maquette la tire du rang dans la liste : créer un fournisseur décalerait alors la couleur de tous
 *    ceux qui le suivent, et un repère qui change de couleur cesse d'en être un.
 */
export function supplierTint(code: string): { bg: string; fg: string } {
  let h = 0;
  for (const c of code) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const tones: TermsChip['tone'][] = ['teal', 'blue', 'violet', 'amber'];
  return TONES[tones[h % tones.length]!];
}

/**
 * Les pastilles du bandeau, dans l'ordre du dessin — vide quand rien n'est saisi.
 * ⚠️ Un e-mail incomplet garde sa pastille, en ROSE : il est enregistré tel quel côté reprise ou import,
 *    et le cacher ferait croire qu'aucune adresse n'est connue.
 */
export function termsChips(s: ReferentialSupplier): TermsChip[] {
  const x = s.terms;
  const out: TermsChip[] = [];
  if (s.coefficientBp !== null) out.push({ key: 'coef', label: t('Coef {v}', { v: coefAffiche(s.coefficientBp) }), tone: 'violet', icon: 'M4 20L20 4M6 4h4v4M14 16h4v4' });
  if (x.discountBp !== null) out.push({ key: 'rem', label: t('Remise {v}', { v: formatRateBp(x.discountBp) }), tone: 'teal', icon: 'M19 5L5 19M7.5 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM16.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z' });
  if (x.earlyPaymentBp !== null) {
    out.push({
      key: 'esc',
      label: x.earlyPaymentDays !== null
        ? t('Escompte {v} à {n} j', { v: formatRateBp(x.earlyPaymentBp), n: x.earlyPaymentDays })
        : t('Escompte {v}', { v: formatRateBp(x.earlyPaymentBp) }),
      tone: 'teal', icon: 'M12 8v4l3 2M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z',
    });
  }
  if (x.shippingCents !== null) out.push({ key: 'port', label: t('Port {v} HT', { v: euros(x.shippingCents) }), tone: 'amber', icon: 'M3 7h13v10H3zM16 10h4l1 3v4h-5' });
  const place = [x.postalCode, x.city].filter(Boolean).join(' ');
  if (place) out.push({ key: 'lieu', label: place, tone: 'blue', icon: 'M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z' });
  if (x.orderEmail) {
    const bad = supplierTermsFieldRefusal('orderEmail', x.orderEmail) !== null;
    out.push({ key: 'mail', label: x.orderEmail, tone: bad ? 'rose' : 'blue', icon: 'M3 5h18v14H3zM3 7l9 6 9-6' });
  }
  if (x.accountCode) out.push({ key: 'cpt', label: x.accountCode, tone: 'ink', icon: 'M4 4h16v16H4zM8 9h8M8 13h8M8 17h5' });
  return out;
}

/** Le bandeau toujours visible : ce qui est saisi, et de quoi ouvrir le panneau. */
export function TermsBanner({ s, open, onToggle }: {
  s: ReferentialSupplier;
  open: boolean;
  onToggle: () => void;
}) {
  const chips = termsChips(s);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, padding: '8px 10px',
      borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', flexWrap: 'wrap',
    }}>
      <span style={{ flex: 1, minWidth: 200, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {chips.map((c) => (
          <span key={c.key} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, height: 24, padding: '0 9px',
            borderRadius: 'var(--r-pill)', background: TONES[c.tone].bg, color: TONES[c.tone].fg,
            fontSize: '11.5px', fontWeight: 700, whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden><path d={c.icon} /></svg>
            {c.label}
          </span>
        ))}
        {chips.length === 0 && (
          <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
            {t('Aucune condition ni coordonnée renseignée — rien ne sera pré-rempli.')}</span>
        )}
      </span>
      <button
        // ⚠️ LE NOM DIT LE FOURNISSEUR, le texte visible en tête : une carte par fournisseur, et un lecteur d’écran annonçait
        //    « Conditions & coordonnées » autant de fois, sans dire lesquelles.
        type="button" onClick={onToggle} aria-expanded={open} aria-label={t('Conditions & coordonnées — {v}', { v: s.label })}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 11px',
          border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
          color: 'var(--ink-800)', fontSize: 'var(--t-dense)', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
        }}
      >
        {t('Conditions & coordonnées')}
        <Icon name="chevronD" size={12} stroke={2.4}
          style={{ transform: `rotate(${open ? 180 : 0}deg)`, transition: 'transform .15s' }} />
      </button>
    </div>
  );
}

/** Ce que l'écran dit d'un refus de la règle — traduit, contrairement à la phrase du serveur. */
export function refusalText(r: SupplierTermsRefusal): string {
  switch (r) {
    case 'rate': return t('un pourcentage entre 0 et 100, deux décimales au plus');
    case 'shipping': return t('un montant positif en euros, deux décimales au plus');
    case 'days': return t('un nombre de jours entre 1 et {max}', { max: SUPPLIER_TERMS_LIMITS.maxEarlyPaymentDays });
    case 'account': return t('chiffres et lettres majuscules, 17 caractères au plus');
    case 'postal_chars': return t('chiffres, lettres, espaces et tirets seulement');
    case 'postal_length': return t('{n} caractère au plus', { n: SUPPLIER_TERMS_LIMITS.maxPostalCodeLength });
    case 'text_length': return t('{n} caractère au plus', { n: SUPPLIER_TERMS_LIMITS.maxTextLength });
    case 'email': return t('adresse incomplète — la commande ne pourrait pas lui être envoyée');
  }
}

/** Écrit un champ des conditions — rend le motif du refus, `null` si accepté ; l'écran relit ensuite. */
export type WriteTerms = (fields: SupplierTermsFields, intention: string) => Promise<string | null>;

/**
 * Le panneau de saisie : conditions commerciales à gauche, coordonnées à droite.
 * ⚠️ Une saisie refusée par la règle n'est PAS envoyée : le champ garde ce qui a été tapé et dit
 *    pourquoi, la valeur enregistrée reste celle du serveur.
 */
export function TermsPanel({ s, write, onCoef }: {
  s: ReferentialSupplier;
  write: WriteTerms;
  /** Rend `true` quand la clause est écrite — `false` fait reprendre à la case la valeur enregistrée. */
  onCoef: (bp: number | null) => Promise<boolean>;
}) {
  const x = s.terms;
  const hint = supplierAccountHint(x.accountCode);
  const hintText = hint === 'unusual'
    ? t('Un compte fournisseur commence en général par 401 — à vérifier avec le cabinet.')
    // ⚠️ AUCUNE PROMESSE D'EXPORT (décision utilisateur Q3) : rien ne lit encore ce compte. Le dire, c'est
    //    éviter qu'on le croie repris dans un fichier que personne n'envoie.
    : t('Aucun écran ne le lit encore — il servira au rapprochement des BL et des factures.');
  return (
    <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
      <PanelSection title={t('Conditions commerciales')} tone="teal"
        icon="M19 5L5 19M7.5 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM16.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <RateInput label={t('Remise fournisseur')} bp={x.discountBp}
            onCommit={(v) => write({ discountBp: v }, `terms:${s.code}:discount`)} />
          <TermsInput label={t('Frais de port')} suffix={t('{symbole} HT', { symbole: currentCurrency().symbol })} value={x.shippingCents === null ? '' : toInputValue(x.shippingCents)}
            placeholder="0" numeric parse={(v) => {
              // À l'échelle de la DEVISE du magasin, comme l'affichage : « 2 » gravé lisait cent fois trop en yens.
              const p = parseMinor(v);
              if (p.state === 'empty') return { ok: true, value: null };
              // ⚠️ Le motif de `parseMinor` est en français : l'écran dit le sien, traduit.
              if (p.state === 'rejected') return { ok: false, reason: refusalText('shipping') };
              return { ok: true, value: p.minor };
            }}
            field="shippingCents"
            onCommit={(v) => write({ shippingCents: v as number | null }, `terms:${s.code}:shipping`)} />
          <RateInput label={t('Escompte')} bp={x.earlyPaymentBp}
            onCommit={(v) => write({ earlyPaymentBp: v }, `terms:${s.code}:early`)} />
          {/* Le repère 1 ne rend rien : le nombre de la phrase est celui que le champ affiche déjà. */}
          <TermsInput label={t('Délai de l’escompte')} suffix={(draftValue) => <Trans k='<1>{n}</1> <2>jour</2>' vars={/^\d+$/.test(draftValue.trim()) ? { n: Number(draftValue) } : undefined} parts={[() => null, (x) => x]} />} value={x.earlyPaymentDays === null ? '' : String(x.earlyPaymentDays)}
            placeholder="—" numeric parse={(v) => {
              const tv = v.trim();
              if (tv === '') return { ok: true, value: null };
              return /^\d+$/.test(tv) ? { ok: true, value: Number(tv) } : { ok: false, reason: t('un nombre de jours entier') };
            }}
            field="earlyPaymentDays"
            onCommit={(v) => write({ earlyPaymentDays: v as number | null }, `terms:${s.code}:early-days`)} />
          <label style={{ gridColumn: '1 / -1' }}>
            <FieldCaption>{t('Coef corner')}</FieldCaption>
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ChampCoef
                valeur={coefAffiche(s.coefficientBp)} placeholder="—"
                heading={t('Coefficient contractuel du fournisseur — hérité par toutes ses marques')}
                largeur={90} hauteur={34} taille="13px" couleur="var(--ink-900)"
                onValidate={(v) => {
                  const bp = coeffInput(v);
                  if (bp === undefined) { showToast(t('Coefficient attendu, par exemple « 2,50 ».'), 'warn'); return Promise.resolve(false); }
                  if (bp === s.coefficientBp) return;
                  return onCoef(bp);
                }}
              />
              <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.4 }}>
                {t('Hérité par toutes ses marques — chaque marque peut le surcharger ci-dessous.')}</span>
            </span>
          </label>
          <div style={{ gridColumn: '1 / -1' }}>
            <TermsInput label={t('Compte comptable fournisseur')} value={x.accountCode ?? ''} placeholder={t('401TEXNORD')}
              mono width={170} uppercase warn={hint === 'unusual'} help={hintText}
              parse={(v) => ({ ok: true, value: v.trim() === '' ? null : v.trim() })}
              field="accountCode"
              onCommit={(v) => write({ accountCode: v as string | null }, `terms:${s.code}:account`)} />
          </div>
        </div>
        <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.45 }}>
          {t('Repris dans chaque nouvelle commande de ce fournisseur — modifiable commande par commande. L’escompte s’affiche au net à payer, il ne change pas le prix d’achat.')}
        </span>
      </PanelSection>
      <PanelSection title={t('Coordonnées')} tone="blue"
        icon="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21zM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z">
        <TextInput label={t('Adresse')} value={x.address1} placeholder={t('N° et rue')} field="address1"
          s={s} write={write} />
        <TextInput label={t('Complément')} value={x.address2} placeholder={t('Bâtiment, BP, ZA… (facultatif)')} field="address2"
          s={s} write={write} />
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10 }}>
          <TextInput label={t('Code postal')} value={x.postalCode} placeholder="59000" field="postalCode" s={s} write={write} />
          <TextInput label={t('Ville')} value={x.city} placeholder={t('Lille')} field="city" s={s} write={write} />
        </div>
        <TextInput label={t('Pays')} value={x.country} placeholder={t('France')} field="country" s={s} write={write} />
        <TextInput label={t('E-mail commandes')} value={x.orderEmail} placeholder={t('commandes@fournisseur.fr')}
          field="orderEmail" s={s} write={write} type="email" />
      </PanelSection>
    </div>
  );
}

/** Un des deux blocs du panneau, avec son repère de tête. */
function PanelSection({ title, tone, icon, children }: { title: string; tone: 'teal' | 'blue'; icon: string; children: ReactNode }) {
  return (
    <div style={{
      border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 10, background: 'var(--paper)',
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 24, height: 24, borderRadius: 'var(--r-xs)', background: TONES[tone].bg, color: TONES[tone].fg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1"
            strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d={icon} /></svg>
        </span>
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: '10px', fontWeight: 600, letterSpacing: '.09em',
          textTransform: 'uppercase', color: 'var(--ink-500)',
        }}>{title}</span>
      </span>
      {children}
    </div>
  );
}

/** Le libellé d'un champ du panneau. */
function FieldCaption({ children }: { children: ReactNode }) {
  return <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 600, color: 'var(--ink-600)', marginBottom: 4 }}>{children}</span>;
}

/** Ce qu'une lecture de saisie rend : la valeur à écrire, ou le motif du refus. */
type ParsedInput = { ok: true; value: number | string | null } | { ok: false; reason: string };

/**
 * Un champ des conditions — il lit, juge, puis écrit à la sortie, et seulement si la valeur a changé.
 * ⚠️ Un refus reste AFFICHÉ sous le champ tant que la saisie n'est pas corrigée : un toast qui disparaît
 *    laisserait la saisie fautive à l'écran, indiscernable d'une valeur enregistrée.
 */
function TermsInput({ label, value, placeholder, suffix, numeric, mono, width, uppercase, warn, help, type, field, parse, onCommit }: {
  label: string;
  value: string;
  placeholder?: string;
  /**
   * L'unité posée dans le champ, à droite. ⚠️ UNE UNITÉ QUI COMPTE se rend d'après la SAISIE en cours : le nombre est
   * dans le champ, la phrase comptée l'accorde (« 1 jour », « 30 jours ») — un mot fixe disait « 1 jours ».
   */
  suffix?: string | ((draftValue: string) => ReactNode);
  numeric?: boolean;
  mono?: boolean;
  width?: number;
  uppercase?: boolean;
  warn?: boolean;
  help?: string;
  type?: string;
  /** Le champ de la règle partagée qui juge la valeur lue. */
  field: SupplierTermsField;
  parse: (v: string) => ParsedInput;
  onCommit: (v: number | string | null) => Promise<string | null>;
}) {
  const [draft, setDraft] = useState(value);
  const [refused, setRefused] = useState<string | null>(null);
  /**
   * La marque éphémère d'une écriture ABOUTIE.
   * ⚠️ LE SUCCÈS SE DIT, COMME L'ÉCHEC (Forme X) : le refus parlait dans le champ, l'écriture réussie se taisait — et
   *    sans bouton « Enregistrer », le silence se lit « rien ne s'est passé ».
   */
  const [saved, setSaved] = useState(false);
  // La valeur du serveur reprend la main dès qu'elle change : c'est lui qui a le dernier mot.
  useEffect(() => { setDraft(value); setRefused(null); }, [value]);

  const commit = () => {
    if (draft === value) { setRefused(null); return; }
    const l = parse(draft);
    if (!l.ok) { setRefused(l.reason); return; }
    const refusal = supplierTermsFieldRefusal(field, l.value);
    if (refusal) { setRefused(refusalText(refusal)); return; }
    setRefused(null);
    // ⚠️ UN REFUS DU SERVEUR SE DIT DANS LE CHAMP : la relecture ne change pas la valeur, donc sans lui la
    //    saisie refusée resterait affichée comme si elle était enregistrée.
    void onCommit(l.value).then((reason) => {
      if (reason) { setRefused(reason); return; }
      setSaved(true);
      // La marque s'efface seule : elle confirme un geste, elle ne décrit pas un état durable.
      setTimeout(() => setSaved(false), 1800);
    });
  };

  const style: CSSProperties = {
    width: width ?? '100%', height: 34, padding: '0 10px', boxSizing: 'border-box',
    border: `1px solid ${refused ? 'var(--rose-fg)' : warn ? 'var(--amber-fg)' : 'var(--ink-200)'}`,
    borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: '13px', color: 'var(--ink-900)',
    outline: 'none',
    ...(numeric ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums', paddingRight: suffix ? (typeof suffix === 'string' && suffix.length <= 2 ? 26 : 40) : 10 } : {}),
    ...(mono ? { fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: '.02em' } : {}),
  };
  const input = (
    <input
      value={draft} type={type} placeholder={placeholder} autoComplete="off"
      inputMode={numeric ? 'decimal' : undefined}
      onChange={(e) => setDraft(uppercase ? e.target.value.toUpperCase() : e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      style={style}
    />
  );
  return (
    <label style={{ display: 'block' }}>
      <FieldCaption>{label}</FieldCaption>
      {suffix ? (
        <span style={{ position: 'relative', display: 'block' }}>
          {input}
          <span style={{ position: 'absolute', right: 10, top: 9, fontSize: 'var(--t-dense)', color: 'var(--ink-400)', pointerEvents: 'none' }}>{typeof suffix === 'function' ? suffix(draft) : suffix}</span>
        </span>
      ) : help !== undefined ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {input}
          <span style={{ flex: 1, minWidth: 160, fontSize: '11.5px', lineHeight: 1.4, color: warn ? 'var(--amber-fg)' : 'var(--ink-500)' }}>{help}</span>
        </span>
      ) : input}
      {refused ? (
        <span role="alert" style={{ display: 'block', marginTop: 4, fontSize: '11.5px', fontWeight: 600, color: 'var(--rose-fg)' }}>
          {t('Non enregistré : {motif}', { motif: refused })}</span>
      ) : saved ? (
        <span role="status" style={{ display: 'block', marginTop: 4, fontSize: '11.5px', fontWeight: 600, color: 'var(--teal-700)' }}>
          {t('Enregistré')}</span>
      ) : null}
    </label>
  );
}

/** Un taux saisi en pour cent (remise, escompte) — lu par la même règle que les taux de TVA. */
function RateInput({ label, bp, onCommit }: { label: string; bp: number | null; onCommit: (v: number | null) => Promise<string | null> }) {
  return (
    <TermsInput label={label} suffix="%" placeholder="0" numeric
      value={bp === null ? '' : formatRateBp(bp, false)}
      parse={(v) => {
        if (v.trim() === '') return { ok: true, value: null };
        const r = parseRateBp(v);
        return r === null ? { ok: false, reason: t('un pourcentage entre 0 et 100, deux décimales au plus') } : { ok: true, value: r };
      }}
      // ⚠️ La même règle pour les deux taux : l'écran ne connaît pas de borne que le serveur ignorerait.
      field="discountBp"
      onCommit={(v) => onCommit(v as number | null)} />
  );
}

/** Un champ de coordonnées — du texte libre, vidé quand on l'efface. */
function TextInput({ label, value, placeholder, field, s, write, type }: {
  label: string;
  value: string | null;
  placeholder: string;
  field: 'address1' | 'address2' | 'postalCode' | 'city' | 'country' | 'orderEmail';
  s: ReferentialSupplier;
  write: WriteTerms;
  type?: string;
}) {
  return (
    <TermsInput label={label} value={value ?? ''} placeholder={placeholder} type={type}
      parse={(v) => ({ ok: true, value: v.trim() === '' ? null : v.trim() })}
      field={field}
      onCommit={(v) => write({ [field]: v as string | null }, `terms:${s.code}:${field}`)} />
  );
}
